/**
 * The filter vocabulary (spec §7.2): the operator table every chip editor renders from,
 * and the compiler that turns one chip into one `filter[]` string. A page never lists
 * operators — the type supplies them — so the vocabulary is identical across every list
 * in the product by construction.
 */
export type FilterFieldType = 'text' | 'number' | 'date' | 'enum' | 'boolean' | 'reference';

/**
 * The adapter seam for day math (spec §7.2's date note). The package cannot know the
 * app's locale or date library, so `on`/`before`/`after` hand the raw string to the
 * adapter and compile whatever bounds come back. `on` is a day — `[startOf('day'),
 * endOf('day')]` — because these are timestamp columns and `$eq` against a date would
 * match only rows stored at exactly midnight. `before` bounds at start-of-day and `after`
 * at end-of-day so "before the 5th" excludes the whole 5th.
 */
export type DateBoundFn = (value: string, kind: 'on' | 'before' | 'after') => { from: string; to: string } | null;

export const OPERATORS_BY_TYPE: Record<FilterFieldType, ReadonlyArray<{ id: string; labelKey: string }>> = {
  text: [
    { id: 'contains', labelKey: 'common.filters.contains' },
    { id: 'is', labelKey: 'common.filters.is' },
    { id: 'starts', labelKey: 'common.filters.starts' },
    { id: 'isNot', labelKey: 'common.filters.isNot' },
    { id: 'isEmpty', labelKey: 'common.filters.isEmpty' },
  ],
  number: [
    { id: 'is', labelKey: 'common.filters.is' },
    { id: 'isNot', labelKey: 'common.filters.isNot' },
    { id: 'gt', labelKey: 'common.filters.gt' },
    { id: 'gte', labelKey: 'common.filters.gte' },
    { id: 'lt', labelKey: 'common.filters.lt' },
    { id: 'lte', labelKey: 'common.filters.lte' },
    { id: 'between', labelKey: 'common.filters.between' },
  ],
  date: [
    { id: 'on', labelKey: 'common.filters.on' },
    { id: 'before', labelKey: 'common.filters.before' },
    { id: 'after', labelKey: 'common.filters.after' },
    { id: 'between', labelKey: 'common.filters.between' },
  ],
  enum: [
    { id: 'is', labelKey: 'common.filters.is' },
    { id: 'isNot', labelKey: 'common.filters.isNot' },
    { id: 'in', labelKey: 'common.filters.in' },
  ],
  reference: [
    { id: 'is', labelKey: 'common.filters.is' },
    { id: 'isNot', labelKey: 'common.filters.isNot' },
    { id: 'in', labelKey: 'common.filters.in' },
  ],
  boolean: [{ id: 'is', labelKey: 'common.filters.is' }],
};

/** A new chip starts on the type's first operator. */
export function defaultOperator(type: FilterFieldType): string {
  return OPERATORS_BY_TYPE[type][0].id;
}

const SERVER_OP: Record<string, string> = {
  contains: '$contL',
  is: '$eq',
  starts: '$startsL',
  isNot: '$ne',
  isEmpty: '$isnull',
  gt: '$gt',
  gte: '$gte',
  lt: '$lt',
  lte: '$lte',
  between: '$between',
  on: '$between',
  before: '$lt',
  after: '$gt',
  in: '$in',
};

/**
 * Normalises the state's `string | string[]` into the value parts the single-value
 * operators compile. Arrays are rejoined losslessly — the URL codec's comma-split is its
 * inverse, so a text value that legitimately contains a comma survives the round trip.
 * (`between`/`in` split their own strings; see compileFilter.)
 */
function valueParts(value: string | string[] | undefined): string[] {
  if (value === undefined) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

function splitCommas(raw: string): string[] {
  return raw.includes(',') ? raw.split(',') : [raw];
}

/**
 * Compiles one chip into one `filter[]` string — `` `${field}||${serverOp}||${value}` `` —
 * or null when the chip contributes nothing. A null is not an error: it is the empty-value
 * rule (spec §7.4, an unfinished chip is not yet a predicate), an arity violation, or an
 * unparseable date, and the caller simply skips it.
 *
 * `type` matters only for `between`, which numbers and dates share: a date range runs from the
 * start of its first day to the END of its last, through the same `dateBound` as `on`. Sent raw,
 * `2026-09-01,2026-09-05` meant midnight on the 5th and dropped everything that day.
 */
export function compileFilter(
  field: string,
  op: string,
  value: string | string[] | undefined,
  dateBound: DateBoundFn,
  type?: FilterFieldType
): string | null {
  if (!field || !op) {
    return null;
  }
  if (op === 'isEmpty') {
    // The missing value IS the predicate: no value segment, no trailing pipes.
    return `${field}||$isnull`;
  }
  const serverOp = SERVER_OP[op];
  if (!serverOp) {
    return null;
  }

  if (op === 'on' || op === 'before' || op === 'after') {
    const parts = valueParts(value);
    if (parts.length !== 1 || parts[0] === '') {
      return null;
    }
    const bound = dateBound(parts[0], op);
    if (!bound) {
      return null;
    }
    if (op === 'on') {
      return `${field}||$between||${bound.from},${bound.to}`;
    }
    if (op === 'before') {
      return `${field}||$lt||${bound.from}`;
    }
    return `${field}||$gt||${bound.to}`;
  }

  if (op === 'between' || op === 'in') {
    const parts = value === undefined ? [] : Array.isArray(value) ? value : splitCommas(value);
    const usable = parts.filter((part) => part !== '');
    if (op === 'between' ? usable.length !== 2 : usable.length === 0) {
      return null;
    }
    if (op === 'between' && type === 'date') {
      const from = dateBound(usable[0], 'on');
      const to = dateBound(usable[1], 'on');
      return from && to ? `${field}||$between||${from.from},${to.to}` : null;
    }
    return `${field}||${serverOp}||${usable.join(',')}`;
  }

  const parts = valueParts(value).filter((part) => part !== '');
  if (parts.length === 0) {
    return null;
  }
  return `${field}||${serverOp}||${parts.join(',')}`;
}
